// ViewDocPanelView — the bespoke variables panel, rendered ON TOP of every
// document (WEL-144 spec §C2).
//
//   Miles: "For any docs that we are viewing such as a lease etc. We'd like all
//   the variables of the doc displayed at the top of the page… exactly what is
//   shown to Miles at the top of the leases he signs in the same format."
//
// This component renders; it never derives. The facts and their wording come
// from the server-side registry's per-kind builder, computed from the frozen
// snapshot of THE VERSION BEING VIEWED — so what you read here is what that
// version says, not what the record says today.

import { Fragment } from 'react';
import { OcEmpty } from '@/components/oc/bits';
import type { ViewDocPanel } from '@/lib/viewDocsClient';

/**
 * A typed-name e-signature, presented as a signature: the name in the
 * signature face on a signature line, the electronic-signing note beneath.
 * Shared by the panel and the form replica so a signed name reads the same
 * everywhere it appears.
 */
export function SignatureBlock({
  name,
  signedAt,
}: {
  name: string;
  signedAt?: string | undefined;
}): React.JSX.Element {
  return (
    <span className="vd-signature" data-testid="vd-signature">
      <span className="vd-signature-name" aria-label={`Signed: ${name}`}>
        {name}
      </span>
      <span className="vd-signature-meta">
        Electronically signed{signedAt ? ` · ${signedAt}` : ''}
      </span>
    </span>
  );
}

export function ViewDocPanelView({ panel }: { panel: ViewDocPanel }): React.JSX.Element {
  // "Empty groups are dropped" (§C2) — a heading with nothing under it is
  // noise, and worse, it reads as data we failed to load.
  const groups = panel.groups.filter((g) => g.rows.length > 0);

  if (groups.length === 0) {
    return <OcEmpty>This version recorded no details.</OcEmpty>;
  }

  // When ANY group is headed, the headless lead group gets the heading's rule
  // without its text, so every column's first row shares one baseline.
  const anyHeaded = groups.some((g) => !!g.heading);

  return (
    <div className="vd-panel-groups">
      {groups.map((group, gi) => (
        <div key={gi} style={{ minWidth: 0 }}>
          {group.heading ? (
            <div className="vd-group-heading">{group.heading}</div>
          ) : (
            anyHeaded && (
              <div className="vd-group-heading" aria-hidden="true">
                &nbsp;
              </div>
            )
          )}
          <div className="vd-rows">
            {group.rows.map((row, ri) => (
              <Fragment key={ri}>
                <span className="vd-row-label">{row.label}</span>
                {row.signature ? (
                  <SignatureBlock name={row.value} signedAt={row.signature.signedAt} />
                ) : (
                  <span
                    className="vd-row-value"
                    data-pending={row.pending ? 'true' : undefined}
                    data-emphasis={row.emphasis ? 'true' : undefined}
                  >
                    {row.value}
                  </span>
                )}
              </Fragment>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
